import type { PrefillLocation, LocationInputComponentResult } from '../LocationInputComponentCore';

import {
    type ValidateStringLocationQuery,
    type ValidateStringLocationQueryVariables,
    type ValidateLocationDataQuery,
    type ValidateLocationDataQueryVariables,
    ValidateStringLocationDocument,
    ValidateLocationDataDocument,
} from '../graphQL/__generated__/locationValidation.gqlgenerated';
import {
    prefillLocationToLocationDataInput,
    isPrefillLocationValid,
} from '../util/locationValidationHelpers';

import { type ApolloError, useApolloClient } from '@apollo/client';
import { useState, useEffect, useCallback } from 'react';

/**
 * State returned by the useValidator hook
 */
export interface ValidatorState {
    /** The validation result */
    result: LocationInputComponentResult;

    /** Whether validation is in progress */
    loading: boolean;

    /** Error from validation, if any */
    error: ApolloError | undefined;
}

/**
 * Options for the useValidator hook
 */
export interface UseValidatorOptions {
    /** Country code for validation (e.g., 'US') */
    country: string;

    /** Language code for validation (e.g., 'en') */
    language: string;

    /** Whether to require precise location in validation */
    preciseLocation: boolean;
}

/**
 * Hook to validate prefilled locations (string or PrefillLocation)
 *
 * Validates the prefill on mount and whenever the prefill value changes.
 * Returns validation state with result, loading, and error.
 *
 * @param prefill - String location or PrefillLocation to validate
 * @param options - Validation options (country, language, preciseLocation)
 * @returns Validator state with result, loading, and error
 */
export const useValidator = (
    prefill: string | PrefillLocation | undefined,
    options: UseValidatorOptions,
): ValidatorState => {
    const client = useApolloClient();

    const { country, language, preciseLocation } = options;

    const [state, setState] = useState<ValidatorState>({
        result: { isValid: false },
        loading: !!prefill, // Start loading if there's a prefill
        error: undefined,
    });

    /**
     * Validates a string location using locationInputComponentStringPrefill
     */
    const validateStringLocation = useCallback(
        async (location: string) => {
            // Skip validation for empty strings
            if (!location || location.trim() === '') {
                setState({
                    result: { isValid: false },
                    loading: false,
                    error: undefined,
                });
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: undefined }));

            try {
                const variables = {
                    location: location.trim(),
                    country: country.toUpperCase(),
                    language: language.toLowerCase(),
                    preciseLocation: preciseLocation,
                };

                const result = await client.query<
                    ValidateStringLocationQuery,
                    ValidateStringLocationQueryVariables
                >({
                    query: ValidateStringLocationDocument,
                    variables,
                    fetchPolicy: 'no-cache',
                });

                const data = result.data?.locationInputComponentStringPrefill;

                if (data?.gen2Location && data?.formattedName) {
                    setState({
                        result: {
                            isValid: true,
                            location: data.gen2Location,
                            formattedName: data.formattedName,
                        },
                        loading: false,
                        error: undefined,
                    });
                } else {
                    setState({
                        result: { isValid: false },
                        loading: false,
                        error: undefined,
                    });
                }
            } catch (err) {
                setState({
                    result: { isValid: false },
                    loading: false,
                    error: err as ApolloError,
                });
            }
        },
        [client, country, language, preciseLocation],
    );

    /**
     * Validates a PrefillLocation using validateLocationData
     */
    const validatePrefillLocation = useCallback(
        async (location: PrefillLocation) => {
            // Check if prefill has enough data
            if (!isPrefillLocationValid(location)) {
                setState({
                    result: { isValid: false },
                    loading: false,
                    error: undefined,
                });
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: undefined }));

            try {
                const locationDataInput = prefillLocationToLocationDataInput(
                    location,
                    country.toUpperCase(),
                );

                const variables = {
                    location: locationDataInput,
                    language: language.toLowerCase(),
                };

                const result = await client.query<
                    ValidateLocationDataQuery,
                    ValidateLocationDataQueryVariables
                >({
                    query: ValidateLocationDataDocument,
                    variables,
                    fetchPolicy: 'no-cache',
                });

                const data = result.data?.validateLocationData;

                if (data?.gen2Location && data?.formattedName) {
                    setState({
                        result: {
                            isValid: true,
                            location: data.gen2Location,
                            formattedName: data.formattedName,
                        },
                        loading: false,
                        error: undefined,
                    });
                } else {
                    setState({
                        result: { isValid: false },
                        loading: false,
                        error: undefined,
                    });
                }
            } catch (err) {
                setState({
                    result: { isValid: false },
                    loading: false,
                    error: err as ApolloError,
                });
            }
        },
        [client, country, language],
    );

    // Validate whenever prefill changes
    useEffect(() => {
        if (typeof prefill === 'string') {
            void validateStringLocation(prefill);
        } else if (prefill && typeof prefill === 'object') {
            void validatePrefillLocation(prefill);
        } else {
            // No prefill - set invalid state
            setState({
                result: { isValid: false },
                loading: false,
                error: undefined,
            });
        }
    }, [prefill, validateStringLocation, validatePrefillLocation]);

    return state;
};
